import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  DialogTitle,
  DialogActions,
  DialogContentText,
  Typography,
  Button,
  styled as muiStyled,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { abyssColors } from '@/theme/colors';
import { useAtom, useAtomValue } from 'jotai';
import * as kpiState from '../../state';
import { usePersistKpiFilters } from '../../hooks/usePersistKpiFilters';
import { mergeKpiAggregatedTemplateFields } from '../../utils';

type DialogColumn = {
  name: string;
  key: string;
  isSelected: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

// Styled components
const Header = muiStyled(DialogTitle)(() => ({
  padding: '3.2rem',
  paddingBottom: '2.4rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const TitleText = muiStyled(Typography)(() => ({
  fontWeight: 600,
  fontSize: '2rem',
  lineHeight: 'normal',
  color: abyssColors.primary[500],
}));

const SubHeading = muiStyled(DialogContentText)(() => ({
  paddingBottom: '2rem',
}));

const Footer = muiStyled(DialogActions)(() => ({
  height: '6.2rem',
  padding: '1.6rem 3.2rem',
  borderTop: '1px solid rgba(101, 130, 164, 0.20)',
}));

type ButtonProps = {
  contained?: boolean;
};

const ActionButton = muiStyled(Button)(({ contained }: ButtonProps) => ({
  padding: '0.8rem 1.8rem',
  height: '3rem',
  borderRadius: '0.4rem',
  fontSize: '1.2rem',
  background: contained ? abyssColors.primary[500] : abyssColors.neutral[50],
  color: contained ? abyssColors.neutral[50] : abyssColors.primary[500],
  boxShadow: 'none',
  textTransform: 'none',
  width: 'fit-content',
  lineHeight: 'normal',
  marginLeft: '0px !important',
  gap: '0.6rem',
  '&:disabled': {
    background: contained ? abyssColors.neutral[300] : abyssColors.neutral[50],
    color: contained ? abyssColors.neutral[50] : abyssColors.primary[300],
  },
  '&:hover': {
    background: contained ? abyssColors.primary[400] : abyssColors.neutral[50],
  },
}));

export const ColumnsSelectionDialog = ({ open, onClose }: Props) => {
  const [visibleColumnKeys, setVisibleColumnKeys] = useAtom(kpiState.visibleColumnKeys);
  const selectedWorklogsTemplate = useAtomValue(kpiState.selectedWorklogsTemplate);
  const selectedIndirectPobLogsTemplateForKpi = useAtomValue(
    kpiState.selectedIndirectPobLogsTemplateForKpi
  );
  const selectedCostLogsTemplateForKpi = useAtomValue(kpiState.selectedCostLogsTemplateForKpi);

  const [originalColumns, setOriginalColumns] = useState<DialogColumn[]>([]);
  const [displayedColumns, setDisplayedColumns] = useState<DialogColumn[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { loading, persistVisibleColumnKeys } = usePersistKpiFilters();

  const templateFields = useMemo(
    () =>
      mergeKpiAggregatedTemplateFields(
        selectedWorklogsTemplate,
        selectedIndirectPobLogsTemplateForKpi,
        selectedCostLogsTemplateForKpi
      ),
    [
      selectedCostLogsTemplateForKpi,
      selectedIndirectPobLogsTemplateForKpi,
      selectedWorklogsTemplate,
    ]
  );

  // Build the columns list from global state and template (excluding Date - always visible)
  const columns: DialogColumn[] = useMemo(() => {
    return templateFields.map((field) => ({
      name: field.name,
      key: field.key ?? '',
      isSelected: (visibleColumnKeys ?? []).includes(field.key ?? ''),
    }));
  }, [templateFields, visibleColumnKeys]);

  // Initialize columns when dialog opens
  useEffect(() => {
    if (open) {
      setOriginalColumns(columns);
      setDisplayedColumns(columns);
      setSearchTerm('');
    }
  }, [columns, open]);

  // Filter columns based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = originalColumns.filter((column) =>
        column.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setDisplayedColumns(filtered);
    } else {
      setDisplayedColumns(originalColumns);
    }
  }, [searchTerm, originalColumns]);

  const handleColumnChange = (key: string) => {
    // Update both original and displayed columns
    const updatedOriginalColumns = originalColumns.map((column) => {
      if (column.key === key) return { ...column, isSelected: !column.isSelected };
      return column;
    });
    setOriginalColumns(updatedOriginalColumns);

    if (searchTerm) {
      const updatedDisplayedColumns = displayedColumns.map((column) => {
        if (column.key === key) return { ...column, isSelected: !column.isSelected };
        return column;
      });
      setDisplayedColumns(updatedDisplayedColumns);
    } else {
      setDisplayedColumns(updatedOriginalColumns);
    }
  };

  const handleSelectAll = () => {
    const allSelected = originalColumns.every((col) => col.isSelected);
    const updatedColumns = originalColumns.map((column) => ({
      ...column,
      isSelected: !allSelected,
    }));
    setOriginalColumns(updatedColumns);
    if (searchTerm) {
      const filtered = updatedColumns.filter((column) =>
        column.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setDisplayedColumns(filtered);
    } else {
      setDisplayedColumns(updatedColumns);
    }
  };

  const handleApply = () => {
    const selectedColumns = originalColumns
      .filter(({ isSelected }) => isSelected)
      .map(({ key }) => key);

    persistVisibleColumnKeys(selectedColumns).then(() => {
      setVisibleColumnKeys(selectedColumns);
      onClose();
    });
  };

  const handleCancel = () => {
    setSearchTerm('');
    onClose();
  };

  const allSelected = originalColumns.every((col) => col.isSelected);
  const someSelected = originalColumns.some((col) => col.isSelected) && !allSelected;

  return (
    <Dialog open={open} onClose={handleCancel} PaperProps={{ sx: { maxWidth: '82rem' } }}>
      <Header>
        <TitleText>Select Columns</TitleText>
      </Header>
      <Box sx={{ padding: '0 4.4rem', marginTop: '2rem' }}>
        <TextField
          fullWidth
          placeholder="Search columns..."
          value={searchTerm}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={(e) => {
            setSearchTerm(e.target.value.toLowerCase());
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '1.4rem',
            },
            '& .MuiInputBase-input': {
              padding: '1rem',
            },
          }}
        />
      </Box>
      <DialogContent sx={{ paddingLeft: '4.4rem' }}>
        <SubHeading>
          Select which columns to display in the Detailed Worklog Report table.
        </SubHeading>
        <Box sx={{ marginBottom: '1.6rem' }}>
          <FormControlLabel
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '1.4rem', fontWeight: 600 } }}
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onClick={handleSelectAll}
              />
            }
            label="Select All"
          />
        </Box>
        <Grid container paddingBottom="4rem" rowSpacing="0.5rem" columnSpacing="1rem">
          {displayedColumns.map(({ name, key, isSelected }) => (
            <Grid item xs={3} key={key}>
              <FormControlLabel
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '1.4rem' } }}
                control={<Checkbox checked={isSelected} onClick={() => handleColumnChange(key)} />}
                label={name}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <Footer>
        <ActionButton onClick={handleCancel} disabled={loading}>
          Cancel
        </ActionButton>
        <ActionButton onClick={handleApply} contained disabled={loading}>
          Apply
        </ActionButton>
      </Footer>
    </Dialog>
  );
};
